/**
 * Photo montage (sections/btf-montage.liquid): up to 20 photos in one of 42 templates, edited
 * frame by frame. Ported from design_handoff_five_makers/personalised-map/montage.js; the
 * templates and the board drawing are in assets/btf-montage-layout.js.
 *
 * Steps: 1 add your photos · 2 choose a template · 3 format and size | 4 colour and spacing ·
 * 5 arrange and edit (the live board beside the photo inspector) · 6 on the wall | 7 order.
 *
 * Without a server for now (Duncan, 5 Oct 2026):
 * - Photos come from the phone's library (which includes iCloud on an iPhone), the computer, or
 *   a drop onto the page, plus a sample album of our own photographs. The Google Photos,
 *   Instagram, Facebook and Dropbox pickers, Centre on faces, Remove background, Upscale and
 *   saving a draft wait for the server.
 * - Photos stay in the browser. The editor works on a copy at most 2000 px long; the print file
 *   is made from the originals at Add to basket (assets/btf-montage-print.js) and attached to the
 *   line, under Shopify's 20 MB limit.
 */

// The templates module, loaded from its versioned address (sections/btf-montage.liquid)
let L, TPL, FAMS, BG, BC, FILTERS, E, filt, imgA, dpiOf, genTree, svgOf, realCm, clamp, rng, capFor, lum, HERO;
const MAX = 20;
const HERO_IDS = ['mosaic', 'hero', 'scatter', 'heart', 'film', 'headline', 'grid', 'pegs', 'torn', 'spiral', 'cover', 'booth', 'year', 'fan'];
const HERO_MS = 3200;
const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
const still = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const mobile = () => window.matchMedia('(max-width: 989px)').matches;
const fmtName = (o) => (o === 'land' ? 'Landscape' : o === 'port' ? 'Portrait' : 'Panoramic');
const ic = (p) => '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="square" stroke-linejoin="round" aria-hidden="true">' + p + '</svg>';
const I = {
  up: ic('<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" x2="12" y1="3" y2="15"/>'),
  grid: ic('<rect width="7" height="7" x="3" y="3"/><rect width="7" height="7" x="14" y="3"/><rect width="7" height="7" x="14" y="14"/><rect width="7" height="7" x="3" y="14"/>'),
  shuffle: ic('<path d="m18 14 4 4-4 4"/><path d="m18 2 4 4-4 4"/><path d="M2 18h1.973a4 4 0 0 0 3.3-1.7l5.454-8.6a4 4 0 0 1 3.3-1.7H22"/><path d="M2 6h1.972a4 4 0 0 1 3.6 2.2"/><path d="M22 18h-6.041a4 4 0 0 1-3.3-1.8l-.359-.45"/>'),
  plus: ic('<path d="M5 12h14"/><path d="M12 5v14"/>'),
  minus: ic('<path d="M5 12h14"/>'),
  undo: ic('<path d="M9 14 4 9l5-5"/><path d="M4 9h10.5a5.5 5.5 0 0 1 5.5 5.5a5.5 5.5 0 0 1-5.5 5.5H11"/>'),
  rl: ic('<path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/>'),
  rr: ic('<path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/>'),
  flip: ic('<path d="m3 7 5 5-5 5V7"/><path d="m21 7-5 5 5 5V7"/><path d="M12 20v2"/><path d="M12 14v2"/><path d="M12 8v2"/><path d="M12 2v2"/>'),
  trash: ic('<path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>'),
  max: ic('<path d="M8 3H3v5M21 8V3h-5M3 16v5h5M16 21h5v-5"/>'),
  prev: ic('<path d="m15 18-6-6 6-6"/>'),
  next: ic('<path d="m9 18 6-6-6-6"/>'),
  x: ic('<path d="M18 6 6 18M6 6l12 12"/>'),
  down: ic('<path d="m6 9 6 6 6-6"/>'),
  up2: ic('<path d="m18 15-6-6-6 6"/>'),
};

// A Size variant's key from its dimensions, whichever way round they are written
function sizeKey(title) {
  const n = (String(title).match(/\d+(\.\d+)?/g) || []).map(Number).sort((a, b) => a - b);
  const k = n.slice(0, 2).join('x');
  return { '40x55': 'Small', '55x75': 'Medium', '75x100': 'Large', '40x75': 'P75', '40x100': 'P100' }[k] || '';
}

class BtfMontage extends HTMLElement {
  #ready = false;
  #pc = 0;
  #last = null;
  #raf = 0;
  #timers = {};
  #busy = false;
  #drag = null;
  #hero = { i: 0, timer: 0, paused: false };
  #roomsInView = false;

  async connectedCallback() {
    if (this.#ready) return;
    this.#ready = true;
    try {
      this.setup = JSON.parse(this.querySelector('script[data-mt-setup]').textContent);
    } catch (e) {
      return;
    }
    [this.roomMod, L] = await Promise.all([import(this.setup.assets.room), import(this.setup.assets.layout)]);
    ({ TPL, FAMS, BG, BC, FILTERS, E, filt, imgA, dpiOf, genTree, svgOf, realCm, clamp, rng, capFor, lum } = L);
    HERO = HERO_IDS.map((id) => TPL.findIndex((t) => t.id === id)).filter((i) => i >= 0);
    this.variants = (this.setup.variants || []).map((v) => Object.assign({}, v, { key: sizeKey(v.title) })).filter((v) => v.key);
    this.uid = 'mt' + Math.random().toString(36).slice(2, 7) + '_';
    const S = (this.S = {
      photos: [], byId: {}, E: {}, t: 4, n: 9, seed: 0, tree: null, order: [], fam: 'All', allT: false,
      fmt: 'land', size: null, bg: 0, bc: 0, gap: 14, border: 0, edge: 'mount', showTitle: true,
      title: 'We Love Cornwall', date: new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }), word: 'HOME', initials: 'A J', number: '40',
      sel: null, caps: null, room: 'desk', msg: '', hint: '', hist: [], pane: 'photo', tsz: 100, album: false,
    });
    S.n = this.#defN(TPL[S.t]);
    this.#regen();
    this.#shell();
    this.#listen();
    this.#watchRooms();
    this.#heroInit();
    this.share = this.querySelector('btf-share');
    if (this.share) {
      this.share.provider = {
        params: () => {
          const p = new URLSearchParams();
          p.set('template', TPL[this.S.t].id);
          p.set('orient', this.S.fmt === 'land' ? 'landscape' : this.S.fmt === 'port' ? 'portrait' : 'panoramic');
          p.set('background', String(this.S.bg));
          return p;
        },
        tokens: () => ({ design: TPL[this.S.t].name + ' photo montage', colour: BG[this.S.bg][0], price: this.#priceText() }),
        picture: () => this.#picture(),
      };
      this.share.hidden = false;
    }
    // A shared link opens its template, format and background (never photos)
    const q = new URLSearchParams(window.location.search);
    const ti = TPL.findIndex((t) => t.id === q.get('template'));
    if (ti >= 0) this.#setTpl(ti, false);
    const o = { landscape: 'land', portrait: 'port', panoramic: 'pano' }[q.get('orient')];
    if (o) S.fmt = o;
    const bg = Number(q.get('background'));
    if (Number.isInteger(bg) && bg >= 0 && bg < BG.length) S.bg = bg;
    window.addEventListener('submit', (e) => this.#submit(e), true);
    window.addEventListener('resize', () => this.#soon('room', () => this.#drawRoom(), 260));
    this.#drawAll();
    this.#demo();
  }

  // The page opens on a finished montage: all twenty sample photos (the 800 px copies, 0.8 MB) in
  // every template, the hero and the rooms. They are marked as samples, so the customer's own
  // photos take their place, and a board can't be ordered while a sample is still on it (they
  // are stock photographs, licensed for the page, not for printing).
  async #demo() {
    // Twenty, the most any template takes, so no template opens with an empty frame
    const S = this.S, list = (this.setup.samples || []).slice(0, MAX);
    if (!list.length || S.photos.length) return;
    const loaded = (await Promise.all(list.map((x) => this.#load(null, x.mid || x.src, x.name)))).filter(Boolean);
    if (S.photos.length) return;
    loaded.forEach((p, i) => Object.assign(p, { sample: true, url: list[i].mid || list[i].src }));
    this.#addMany(loaded, null);
    S.sel = null;
    S.hist = [];
    this.#commit();
    this.#heroDraw();
  }

  // The customer's first photos replace the opening samples
  #dropDemo() {
    const S = this.S;
    if (!S.photos.length || !S.photos.every((p) => p.sample)) return;
    S.photos = [];
    S.byId = {};
    S.order = S.order.map(() => null);
    S.sel = null;
  }

  /* -------------------------------------------------------------- helpers */

  q(s) {
    return this.querySelector(s);
  }

  #soon(key, fn, ms) {
    clearTimeout(this.#timers[key]);
    this.#timers[key] = setTimeout(fn, ms);
  }

  get tp() {
    return TPL[this.S.t];
  }

  #defN(tp) {
    return tp.fixed ? tp.fixed(this.S) : clamp(this.S.photos.length || tp.def, tp.min, tp.max);
  }

  #fillOrder(prev, n) {
    const S = this.S;
    const keep = [];
    prev.forEach((p) => {
      if (p && S.byId[p] && !keep.includes(p)) keep.push(p);
    });
    S.photos.forEach((p) => {
      if (!keep.includes(p.id)) keep.push(p.id);
    });
    const a = keep.slice(0, n);
    while (a.length < n) a.push(null);
    return a;
  }

  #regen() {
    const S = this.S;
    S.tree = genTree(S, S.fmt);
    S.order = this.#fillOrder(S.order, S.n);
    if (S.sel != null && S.sel >= S.n) S.sel = null;
  }

  #snap() {
    const S = this.S;
    S.hist.push(JSON.stringify({ E: S.E, order: S.order, tree: S.tree, n: S.n, seed: S.seed, t: S.t }));
    if (S.hist.length > 40) S.hist.shift();
  }

  // The board in cm, and the variant for the chosen size
  get cm() {
    return realCm(this.S, this.S.fmt);
  }

  #variant() {
    const S = this.S;
    return S.size ? this.variants.find((v) => v.key === S.size) : null;
  }

  #priceText() {
    const v = this.#variant();
    return v ? v.price : this.setup.from;
  }

  #capsHtml(cm, col) {
    const [w, h] = cm.map((v) => v * 10);
    const cx = (L.HOLE_MM / w) * 100, cy = (L.HOLE_MM / h) * 100, tw = (L.CAP_TILE_MM / w) * 100, th = (L.CAP_TILE_MM / h) * 100;
    const url = col === 'black' ? this.setup.assets.capBlack : this.setup.assets.capWhite;
    return [[cx, cy], [100 - cx, cy], [cx, 100 - cy], [100 - cx, 100 - cy]]
      .map(([x, y]) => `<span class="btf-mk-board__cap" style="left: ${(x - tw / 2).toFixed(3)}%; top: ${(y - th / 2).toFixed(3)}%; width: ${tw.toFixed(3)}%; height: ${th.toFixed(3)}%; background-image: url('${url}');"></span>`)
      .join('');
  }

  // Paints a board element with a state (the live board, a card, a room, the hero)
  #paintBoard(el, st, fmt, pre, edit, caps) {
    const cm = realCm(st, fmt);
    el.className = 'btf-mk-board is-' + fmt + ' btf-mt-board';
    el.style.setProperty('--bg', BG[st.bg][1]);
    el.style.setProperty('--mk-ar', cm[0] + ' / ' + cm[1]);
    el.style.setProperty('--mk-rad', ((L.RAD_MM / (cm[0] * 10)) * 100).toFixed(3) + '% / ' + ((L.RAD_MM / (cm[1] * 10)) * 100).toFixed(3) + '%');
    const r = svgOf(st, fmt, pre, edit);
    el.innerHTML = r.html + (caps ? this.#capsHtml(cm, caps) : '');
    return r.P;
  }

  // A template as its card shows it: the customer's photos, in order, at its own count
  #thumbState(i, fmt) {
    const S = this.S, tp = TPL[i];
    const t = Object.assign({}, S, { t: i, seed: 0, sel: null, fmt: fmt || S.fmt });
    t.n = tp.fixed ? tp.fixed(S) : clamp(S.photos.length || tp.def, tp.min, tp.max);
    t.tree = genTree(t, t.fmt);
    const ids = S.photos.map((p) => p.id);
    t.order = Array.from({ length: t.n }, (_, k) => ids[k] || null);
    return t;
  }

  /* ---------------------------------------------------------------- shell */

  #step(n, title, aside, id) {
    return `<div class="btf-mk-step__head"><h2 class="btf-mk-step__title" id="${id}-title"><span class="btf-mk-step__n">Step ${n}</span><span class="btf-hb">${title}</span></h2>${aside ? `<p class="btf-mk-step__aside">${aside}</p>` : ''}</div>`;
  }

  #shell() {
    const L0 = this.setup.labels || {};
    const box = this.q('[data-mt-root]');
    box.innerHTML = `
      <div class="btf-mt-s1">
        <section class="btf-mk-step" id="mt-step-1" aria-labelledby="mt-step-1-title">
          ${this.#step(1, 'Add your photos', 'Up to 20 photos, from your phone or your computer', 'mt-step-1')}
          <div class="btf-mt-src">
            <label class="btf-mt-src__pri"><input type="file" accept="image/*" multiple data-mt-files class="visually-hidden">${I.up}<span><b class="btf-mt-src__phone">Choose from your phone</b><b class="btf-mt-src__desk">Upload from computer</b><small class="btf-mt-src__phone">Your photo library or camera</small><small class="btf-mt-src__desk">Or drag photos onto this page</small></span></label>
            <button type="button" data-mt-album><span>Sample album</span><small>Try it with ours</small></button>
          </div>
          <div data-mt-albumbox></div>
          <div class="btf-mt-trayhd"><span class="btf-mk-lbl" data-mt-count></span><span><span class="btf-mt-trayhint" data-mt-trayhint></span><button type="button" class="btf-mt-link" data-mt-clear>Remove all</button></span></div>
          <div class="btf-mt-tray" data-mt-tray></div>
        </section>
      </div>
      <div class="btf-mk-s1">
        <section class="btf-mk-step" id="mt-step-2" aria-labelledby="mt-step-2-title">
          ${this.#step(2, 'Choose a template', TPL.length + ' templates. Each one adapts to the number of photos you add', 'mt-step-2')}
          <div class="btf-mt-fams" data-mt-fams></div>
          <div class="btf-mk-cards btf-mt-cards" data-mt-tpls></div>
          <div class="btf-mt-more" data-mt-tmore></div>
        </section>
      </div>
      <div class="btf-mk__row btf-mk__row--23">
        <section class="btf-mk-step" id="mt-step-3" aria-labelledby="mt-step-3-title">
          ${this.#step(3, 'Format and size', '', 'mt-step-3')}
          <div class="btf-mk-tabs btf-mt-fmt" role="group" aria-label="Format" data-mt-fmt></div>
          <div class="btf-mk-sizes" role="group" aria-label="Size" data-mt-sizes></div>
          <p class="btf-mk-sizehelp" data-mt-sizehelp></p>
        </section>
        <section class="btf-mk-step" id="mt-step-4" aria-labelledby="mt-step-4-title">
          ${this.#step(4, 'Colour and spacing', '', 'mt-step-4')}
          <div class="btf-mt-grp"><span class="btf-mk-lbl">Background</span><div class="btf-mk-swatches" data-mt-bgs></div></div>
          <div class="btf-mt-grp"><span class="btf-mk-lbl">Borders and print frames</span><div class="btf-mk-swatches" data-mt-bcs></div></div>
          <div class="btf-mt-grp">
            <label class="btf-mt-sld"><span class="btf-mk-lbl">Gap</span><input class="btf-mk-range" type="range" min="0" max="40" step="1" value="14" data-mt-k="gap"><em data-mt-v="gap">14</em></label>
            <label class="btf-mt-sld"><span class="btf-mk-lbl">Border</span><input class="btf-mk-range" type="range" min="0" max="14" step="1" value="0" data-mt-k="border"><em data-mt-v="border">0</em></label>
          </div>
          <div class="btf-mt-grp"><span class="btf-mk-lbl">Edge</span><div class="btf-mk-tabs btf-mt-edge"><button type="button" class="btf-mk-tab" data-mt-edge="mount">Inside the fixings</button><button type="button" class="btf-mk-tab" data-mt-edge="bleed">Edge to edge</button></div>
          <p class="btf-mt-note">Inside the fixings keeps your photos 45 mm in from the edge, just clear of the four screw caps, and the background colour runs right to the edge. Edge to edge takes your photos to the edge too, and the holes are drilled through the corner photos.</p></div>
        </section>
      </div>
      <div class="btf-mt-s5h" id="mt-step-5">
        ${this.#step(5, 'Arrange and edit', 'Place, swap and edit your photos on the board. The words go on the second tab.', 'mt-step-5')}
        <div class="btf-mt-tools" data-mt-tools></div>
      </div>
      <div class="btf-mk__row btf-mk__row--4p btf-mt-row5">
        <div class="btf-mt-pv">
          <div class="btf-mt-stick">
            <div class="btf-mk-stage btf-mt-stage" data-mt-stage><span class="btf-mk-stage__badge">Your montage · live preview</span><div class="btf-mk-board" data-mt-board></div></div>
            <p class="btf-mt-hint"><span class="btf-mt-src__desk">Click a photo to edit it. Drag one photo onto another to swap them. Drag the selected photo to move it in its frame and scroll to zoom. Drag the dark handles to resize frames.</span><span class="btf-mt-src__phone">Tap a photo to edit it. Drag a photo onto another to swap them, or drag the selected photo to move it in its frame.</span></p>
            <div class="btf-mk-caps" id="mt-caps"><span class="btf-mk-lbl">Screw caps</span><div class="btf-mk-tabs"><button type="button" class="btf-mk-tab" data-mt-cap="white">White</button><button type="button" class="btf-mk-tab" data-mt-cap="black">Black</button></div></div>
            <div data-mt-share></div>
          </div>
        </div>
        <div class="btf-mk-step btf-mt-side">
          <div class="btf-mk-tabs btf-mt-panes"><button type="button" class="btf-mk-tab" data-mt-pane="photo">Edit a photo</button><button type="button" class="btf-mk-tab" data-mt-pane="words">Title and words</button></div>
          <div class="btf-mt-pn" data-mt-ins></div>
          <div class="btf-mt-pn" data-mt-words></div>
        </div>
      </div>
      <div class="btf-mk__row btf-mk__row--56">
        <section class="btf-mk-step" id="mt-step-6" aria-labelledby="mt-step-6-title">
          ${this.#step(6, 'See it on the wall', 'Shown at true size for the size you pick', 'mt-step-6')}
          <div class="btf-mk-tabs btf-mk-roomtabs" data-mt-rooms>${['desk|Desk', 'kitchen|Kitchen', 'studio|Studio', 'office|Office'].map((r) => { const [k, n] = r.split('|'); return `<button type="button" class="btf-mk-tab" data-mt-room="${k}">${n}</button>`; }).join('')}</div>
          <div class="btf-mk-room" data-mt-room-stage role="img" aria-label="Your board shown on a wall, at its true size"><div class="btf-mk-room__scene"><span class="btf-mk-room__base"></span><div class="btf-mk-room__art"><span class="btf-mk-board" data-mt-room-board></span></div><span class="btf-mk-room__skin"></span><span class="btf-mk-room__gloss"></span><span class="btf-mk-room__cap"></span><span class="btf-mk-room__cap"></span><span class="btf-mk-room__cap"></span><span class="btf-mk-room__cap"></span></div></div>
          <p class="btf-mk-roomnote" data-mt-roomnote></p>
        </section>
        <section class="btf-mk-step" id="mt-step-7" aria-labelledby="mt-step-7-title">
          ${this.#step(7, 'Order your board', '', 'mt-step-7')}
          <dl class="btf-mk-rows" data-mt-rows></dl>
          <div class="btf-mk-price"><span class="btf-mk-lbl">Price</span><span class="btf-h" data-mt-price></span></div>
          <div data-mt-formslot></div>
          <p class="btf-mk-msg" data-mt-msg role="alert" hidden></p>
          <p class="btf-mk-note">${esc(L0.orderNote || 'Made to order. After you order we email you a proof at full print resolution to approve before anything is printed.')}</p>
        </section>
      </div>
      <div class="btf-mk-lb" data-mt-lb hidden></div>`;
    // The form and the share strip are rendered by Liquid; move them into place
    const form = this.q('form[data-mt-form]');
    if (form) this.q('[data-mt-formslot]').appendChild(form);
    const share = this.q('[data-mt-sharesrc] > *');
    if (share) this.q('[data-mt-share]').appendChild(share);
    this.form = form;
    this.board = this.q('[data-mt-board]');
  }

  /* --------------------------------------------------------------- drawing */

  #drawAll() {
    this.#drawFams();
    this.#drawColours();
    this.#drawFmt();
    this.#drawWords();
    this.#drawTpls();
    this.#drawAlbum();
    this.#commit();
  }

  #drawBoard() {
    const S = this.S;
    this.#last = this.#paintBoard(this.board, S, S.fmt, this.uid, true, S.caps || capFor(BG[S.bg][1]));
    this.board.style.touchAction = S.sel != null ? 'none' : 'pan-y';
    this.q('[data-mt-stage]').classList.toggle('is-port', S.fmt === 'port');
  }

  #rafBoard() {
    if (this.#raf) return;
    this.#raf = requestAnimationFrame(() => {
      this.#raf = 0;
      this.#drawBoard();
    });
  }

  #liveImg(i) {
    const c = this.#last.cells[i], pid = this.S.order[i], el = this.board.querySelector('[data-img="' + i + '"]');
    if (!el || !c) return;
    const e = E(this.S, pid), a = imgA(c, this.S.byId[pid], e), f = (n) => Math.round(n * 10) / 10;
    el.setAttribute('x', f(a.x));
    el.setAttribute('y', f(a.y));
    el.setAttribute('width', f(a.w));
    el.setAttribute('height', f(a.h));
    el.setAttribute('transform', a.tf);
    el.style.filter = filt(e);
  }

  #lowCount() {
    const S = this.S;
    if (!this.#last) return 0;
    let n = 0;
    this.#last.cells.forEach((c, i) => {
      const p = S.order[i];
      if (p && S.byId[p] && !S.byId[p].sample && dpiOf(S, c, S.byId[p], E(S, p), S.fmt) < 150) n++;
    });
    return n;
  }

  #drawTray() {
    const S = this.S, t = this.q('[data-mt-tray]'), used = {};
    S.order.forEach((p, i) => {
      if (p) used[p] = i + 1;
    });
    const demo = S.photos.length && S.photos.every((p) => p.sample);
    this.q('[data-mt-count]').textContent = (demo ? 'Sample photos · ' : 'Your photos · ') + S.photos.length + ' of ' + MAX;
    this.q('[data-mt-clear]').hidden = !S.photos.length;
    this.q('[data-mt-trayhint]').textContent = S.hint || (demo ? 'These sample photos show the templates. Add your own and they take their place.' : S.sel != null ? 'Tap a photo to put it in frame ' + (S.sel + 1) : S.photos.length ? 'Drag a photo onto the board, or select a frame first' : '');
    t.innerHTML = S.photos.length
      ? S.photos.map((p) => {
          const i = S.order.indexOf(p.id);
          const low = !p.sample && i >= 0 && this.#last && this.#last.cells[i] && dpiOf(S, this.#last.cells[i], p, E(S, p.id), S.fmt) < 150;
          return `<span class="btf-mt-t${i >= 0 && i === S.sel ? ' is-on' : ''}" draggable="true" data-mt-tray="${p.id}" title="${esc(p.name)}"><img src="${p.src}" alt="">${used[p.id] ? `<b>${used[p.id]}</b>` : ''}${low ? '<i>!</i>' : ''}<button type="button" data-mt-rm="${p.id}" aria-label="Remove photo">×</button></span>`;
        }).join('')
      : `<div class="btf-mt-empty"><b>No photos yet.</b><span>Choose photos from your phone or computer, drag them onto this page, or try the sample album.</span></div>`;
  }

  #drawAlbum() {
    const box = this.q('[data-mt-albumbox]');
    const btn = this.q('[data-mt-album]');
    btn.classList.toggle('is-on', this.S.album);
    btn.setAttribute('aria-expanded', String(this.S.album));
    if (!this.S.album) {
      box.innerHTML = '';
      return;
    }
    // A sample already on the board, whichever copy it came in as (the opening montage uses the
    // 800 px copies, the album the 1600 px ones), is not offered again
    const have = this.S.photos.map((x) => x.url);
    const left = (this.setup.samples || []).filter((x) => !have.includes(x.src) && !have.includes(x.mid));
    box.innerHTML = `<div class="btf-mt-panel"><div class="btf-mt-trayhd"><span class="btf-mk-lbl">Sample album</span><button type="button" class="btf-mt-link" data-mt-album>Close</button></div><p>Tap a photo to add it to your montage.</p><div class="btf-mt-album">${left.length ? left.map((x) => `<button type="button" data-mt-sample="${(this.setup.samples || []).indexOf(x)}" aria-label="${esc(x.name)}"><img src="${x.thumb || x.src}" alt="" loading="lazy"></button>`).join('') : '<p>Every sample photo is already in your montage.</p>'}</div></div>`;
  }

  #drawFams() {
    this.q('[data-mt-fams]').innerHTML = FAMS.map((x) => `<button type="button" class="btf-mk-tab${x === this.S.fam ? ' is-on' : ''}" data-mt-fam="${x}">${x}</button>`).join('');
  }

  #drawTpls() {
    const S = this.S, box = this.q('[data-mt-tpls]'), o = S.fmt;
    const per = mobile() ? 4 : 6;
    const list = TPL.map((tp, i) => i).filter((i) => S.fam === 'All' || TPL[i].fam === S.fam);
    let show = list;
    if (!S.allT && list.length > per) {
      show = list.slice(0, per);
      if (list.includes(S.t) && !show.includes(S.t)) show[per - 1] = S.t;
    }
    const more = this.q('[data-mt-tmore]'), hid = list.length - show.length;
    more.innerHTML = list.length > per ? `<button type="button" class="btf-mt-tog" data-mt-tall aria-expanded="${!!S.allT}"><span>${S.allT ? 'Show fewer' : 'Show all ' + list.length + ' ' + (S.fam === 'All' ? 'templates' : S.fam.toLowerCase() + ' templates')}</span><small>${S.allT ? '' : hid + ' more'}</small>${S.allT ? I.up2 : I.down}</button>` : '';
    box.style.setProperty('--mk-cols', mobile() ? 2 : 6);
    box.innerHTML = show
      .map((i) => {
        const tp = TPL[i];
        return `<div class="btf-mk-card${i === S.t ? ' is-on' : ''}" data-mt-card="${i}"><button type="button" class="btf-mk-card__pick" data-mt-tpl="${i}" aria-pressed="${i === S.t}"><span class="btf-mk-card__plate"><span class="btf-mk-board" data-mt-th="${i}"></span></span><span class="btf-mk-card__name"><b>${String(i + 1).padStart(2, '0')}</b>${tp.name}</span><span class="btf-mk-card__desc">${tp.desc} · ${tp.fixed ? tp.fixed(S) + ' photos' : tp.min + '–' + tp.max + ' photos'}</span></button><button type="button" class="btf-mk-zoom" data-mt-zoom="${i}" aria-label="Preview ${esc(tp.name)} larger">${I.max}</button></div>`;
      })
      .join('');
    box.querySelectorAll('[data-mt-th]').forEach((el) => {
      const i = +el.dataset.mtTh;
      this.#paintBoard(el, this.#thumbState(i), o, this.uid + 'th' + i + '_', false, capFor(BG[S.bg][1]));
    });
  }

  #markTpls() {
    this.querySelectorAll('[data-mt-card]').forEach((el) => {
      const on = +el.dataset.mtCard === this.S.t;
      el.classList.toggle('is-on', on);
      el.querySelector('[data-mt-tpl]').setAttribute('aria-pressed', String(on));
    });
  }

  #chips(list, cur, key) {
    return list.map((c, i) => `<button type="button" class="btf-mk-swatch${i === cur ? ' is-on' : ''}" data-mt-${key}="${i}" aria-pressed="${i === cur}"><span class="btf-mk-swatch__chip" style="background: ${c[1]}; color: ${lum(c[1]) > 0.5 ? '#1d1d1b' : '#ffffff'};">Aa</span><span class="btf-mk-swatch__name">${c[0]}</span></button>`).join('');
  }

  #drawColours() {
    const S = this.S;
    this.q('[data-mt-bgs]').innerHTML = this.#chips(BG, S.bg, 'bg');
    this.q('[data-mt-bcs]').innerHTML = this.#chips(BC, S.bc, 'bc');
    this.querySelectorAll('[data-mt-edge]').forEach((b) => b.classList.toggle('is-on', b.dataset.mtEdge === S.edge));
  }

  #drawFmt() {
    const S = this.S;
    this.q('[data-mt-fmt]').innerHTML = ['land', 'port', 'pano'].map((k) => `<button type="button" class="btf-mk-tab${k === S.fmt ? ' is-on' : ''}" data-mt-fset="${k}" aria-pressed="${k === S.fmt}">${fmtName(k)}</button>`).join('');
    const keys = S.fmt === 'pano' ? ['P75', 'P100'] : ['Small', 'Medium', 'Large'];
    const names = { Small: 'Small', Medium: 'Medium', Large: 'Large', P75: 'Panoramic', P100: 'Panoramic' };
    const sizes = this.q('[data-mt-sizes]');
    sizes.classList.toggle('is-two', keys.length === 2);
    sizes.innerHTML = keys
      .filter((k) => this.variants.some((v) => v.key === k))
      .map((k) => {
        const cm = realCm(Object.assign({}, S, { size: k }), S.fmt);
        return `<button type="button" class="btf-mk-size${S.size === k ? ' is-on' : ''}" data-mt-size="${k}" aria-pressed="${S.size === k}"><span class="btf-mk-lbl">${names[k]}</span><span>${cm[0]} × ${cm[1]} cm</span></button>`;
      })
      .join('');
    const v = this.#variant();
    this.q('[data-mt-sizehelp]').textContent = v ? '' : 'Choose a size to see its price. We check your photos at ' + (S.fmt === 'pano' ? '100 × 40 cm' : 'Large') + ' until you do.';
  }

  #drawTools() {
    const S = this.S, tp = this.tp, flex = !tp.fixed;
    this.q('[data-mt-tools]').innerHTML =
      `<button type="button" class="btf-mt-b" data-mt-act="auto" title="Put the photos in the frames that suit their shape">${I.grid}Auto-fill</button>` +
      `<button type="button" class="btf-mt-b" data-mt-act="shuffle" title="Try another arrangement">${I.shuffle}Shuffle</button>` +
      `<button type="button" class="btf-mt-b" data-mt-act="add"${flex && S.n < tp.max && S.n < MAX ? '' : ' disabled'}>${I.plus}Add a frame</button>` +
      `<button type="button" class="btf-mt-b" data-mt-act="remove"${flex && S.n > tp.min ? '' : ' disabled'}>${I.minus}${S.sel != null && flex ? 'Remove this frame' : 'Remove a frame'}</button>` +
      `<button type="button" class="btf-mt-b" data-mt-act="undo"${S.hist.length ? '' : ' disabled'}>${I.undo}Undo</button>`;
  }

  #qbox() {
    const S = this.S, i = S.sel, pid = S.order[i], c = this.#last && this.#last.cells[i];
    if (!pid || !c) return '';
    // A sample is a small display copy that can't be ordered: no print check, just what it is
    if (S.byId[pid] && S.byId[pid].sample) return `<div class="btf-mt-q" data-mt-qbox><b>Sample photo.</b><span>It shows the template. Swap it for your own before you order.</span></div>`;
    const e = E(S, pid), d = dpiOf(S, c, S.byId[pid], e, S.fmt);
    const sz = S.fmt === 'pano' ? 'the panoramic board' : (S.size || 'Large') + (S.size ? '' : ' (until you choose a size)');
    return d < 150
      ? `<div class="btf-mt-q is-warn" data-mt-qbox><b>May print soft at ${sz}.</b><span>This photo prints at about ${d} dpi in this frame. We recommend 150 or more. Zoom out, or give it a bigger frame. You can still order, and we will point it out on your proof.</span></div>`
      : `<div class="btf-mt-q" data-mt-qbox><b>Sharp at ${sz}.</b><span>Prints at about ${d} dpi in this frame.</span></div>`;
  }

  #drawIns() {
    const S = this.S, box = this.q('[data-mt-ins]'), i = S.sel, tp = this.tp;
    if (i == null) {
      box.innerHTML = `<div class="btf-mt-none"><b>Choose a photo on the board to edit it.</b><span>Crop and zoom it, turn or flip it, change how it looks, or add a caption. Every change shows on the board straight away and can be undone.</span></div>`;
      return;
    }
    const pid = S.order[i];
    const head = (img, small) => `<div class="btf-mt-ihd">${img}<span><span class="btf-hb">Frame ${i + 1} of ${this.#last.cells.length}</span><small>${small}</small></span><button type="button" class="btf-mt-b" data-mt-ia="done">Done</button></div>`;
    if (!pid) {
      box.innerHTML = `<div class="btf-mt-ins">${head('<span class="btf-mt-ihd__blank"></span>', 'Empty')}<div class="btf-mt-none"><b>Choose a photo for this frame.</b><span>Tap a photo in your tray above, or upload a new one.</span></div><div class="btf-mt-tools"><label class="btf-mt-b is-red"><input type="file" accept="image/*" data-mt-replace class="visually-hidden">${I.up}Upload a photo</label>${!tp.fixed && S.n > tp.min ? `<button type="button" class="btf-mt-b" data-mt-act="remove">${I.minus}Remove this frame</button>` : ''}</div></div>`;
      return;
    }
    const ph = S.byId[pid], e = E(S, pid), cell = this.#last.cells[i];
    box.innerHTML =
      `<div class="btf-mt-ins">${head(`<img src="${ph.src}" alt="" style="filter: ${filt(e) || 'none'};">`, esc(ph.name) + ' · ' + ph.w + ' × ' + ph.h + ' px')}` +
      this.#qbox() +
      `<div class="btf-mt-grp"><span class="btf-mk-lbl">Crop</span><label class="btf-mt-sld"><span>Zoom</span><input class="btf-mk-range" type="range" min="1" max="4" step="0.01" value="${e.z}" data-mt-ik="z"><em data-mt-iv="z">${Math.round(e.z * 100)}%</em></label><div class="btf-mt-tools"><button type="button" class="btf-mt-b" data-mt-ia="reset">Reset crop</button></div></div>` +
      `<div class="btf-mt-grp"><span class="btf-mk-lbl">Turn</span><div class="btf-mt-tools"><button type="button" class="btf-mt-b" data-mt-ia="rl">${I.rl}Left</button><button type="button" class="btf-mt-b" data-mt-ia="rr">${I.rr}Right</button><button type="button" class="btf-mt-b${e.flip ? ' is-on' : ''}" data-mt-ia="flip" aria-pressed="${e.flip}">${I.flip}Flip</button></div></div>` +
      `<div class="btf-mt-grp"><span class="btf-mk-lbl">Look</span><div class="btf-mt-filt">${FILTERS.map((x) => `<button type="button" class="${x[0] === e.f ? 'is-on' : ''}" data-mt-filt="${x[0]}" aria-pressed="${x[0] === e.f}"><img src="${ph.src}" alt="" style="filter: ${x[2] || 'none'};">${x[1]}</button>`).join('')}</div>` +
      `<label class="btf-mt-sld"><span>Brightness</span><input class="btf-mk-range" type="range" min="50" max="150" step="1" value="${e.b}" data-mt-ik="b"><em data-mt-iv="b">${e.b}</em></label><label class="btf-mt-sld"><span>Contrast</span><input class="btf-mk-range" type="range" min="50" max="150" step="1" value="${e.c}" data-mt-ik="c"><em data-mt-iv="c">${e.c}</em></label></div>` +
      (cell.shape ? '' : `<div class="btf-mk-field"><span class="btf-mk-field__row"><label class="btf-mk-lbl" for="${this.uid}cap">Caption <span class="btf-mk-field__opt">(optional)</span></label><em data-mt-cc>${e.cap.length} / 28</em></span><input class="btf-mk-input" id="${this.uid}cap" data-mt-ik="cap" maxlength="28" value="${esc(e.cap)}" placeholder="Polperro, day one"></div>`) +
      `<div class="btf-mt-tools"><label class="btf-mt-b"><input type="file" accept="image/*" data-mt-replace class="visually-hidden">${I.up}Replace photo</label><button type="button" class="btf-mt-b" data-mt-ia="clear">${I.trash}Empty frame</button></div></div>`;
  }

  #drawWords() {
    const S = this.S, tp = this.tp, id = tp.id, own = tp.title !== 'none';
    const curAl = (k) => (k === 'ta' ? S.ta || 'left' : S.da || (id === 'headline' || id === 'cover' ? 'left' : 'right'));
    const al = (k, label, show) => (show ? `<div class="btf-mk-field is-full"><span class="btf-mk-lbl">${label}</span><div class="btf-mk-tabs">${[['left', 'Left'], ['centre', 'Centre'], ['right', 'Right']].map(([v, nm]) => `<button type="button" class="btf-mk-tab${curAl(k) === v ? ' is-on' : ''}" data-mt-al="${k}|${v}">${nm}</button>`).join('')}</div></div>` : '');
    const fld = (k, label, max, show, ph) => (show ? `<div class="btf-mk-field is-full"><span class="btf-mk-field__row"><label class="btf-mk-lbl" for="${this.uid}${k}">${label}</label></span><input class="btf-mk-input" id="${this.uid}${k}" data-mt-k="${k}" maxlength="${max}" value="${esc(S[k])}"${ph ? ` placeholder="${ph}"` : ''}></div>` : '');
    const titled = own && S.showTitle;
    this.q('[data-mt-words]').innerHTML =
      (own ? `<div class="btf-mk-tabs btf-mt-ttl"><button type="button" class="btf-mk-tab${S.showTitle ? ' is-on' : ''}" data-mt-ttl="1">With a title</button><button type="button" class="btf-mk-tab${S.showTitle ? '' : ' is-on'}" data-mt-ttl="0">Photos only</button></div>` : '<p class="btf-mt-note">The contact sheet has no title. Use captions to label the frames.</p>') +
      `<div class="btf-mk-fields">` +
      fld('title', 'Title', 32, titled) +
      fld('date', id === 'year' ? 'Line beside the title' : 'Date or second line', 28, titled) +
      (titled ? `<div class="btf-mk-field is-full"><span class="btf-mk-field__row"><label class="btf-mk-lbl" for="${this.uid}tsz">Text size</label><em data-mt-tszv>${S.tsz}%</em></span><input class="btf-mk-range" id="${this.uid}tsz" type="range" min="70" max="130" step="5" value="${S.tsz}" data-mt-k="tsz"></div>` : '') +
      al('ta', 'Title alignment', titled) +
      al('da', (id === 'year' ? 'Line' : 'Date') + ' alignment', titled) +
      fld('word', 'Word (up to 6 letters)', 6, id === 'word') +
      fld('initials', 'Initials (2 or 3)', 5, id === 'initials', 'A J') +
      fld('number', 'Number (up to 4)', 4, id === 'number') +
      `</div>`;
  }

  #drawPane() {
    const p = this.S.pane;
    this.q('[data-mt-ins]').hidden = p !== 'photo';
    this.q('[data-mt-words]').hidden = p !== 'words';
    this.querySelectorAll('[data-mt-pane]').forEach((b) => {
      b.classList.toggle('is-on', b.dataset.mtPane === p);
      b.setAttribute('aria-pressed', String(b.dataset.mtPane === p));
    });
  }

  #drawSummary() {
    const S = this.S, tp = this.tp, frames = this.#last ? this.#last.cells.length : S.n;
    // Anonymous counts (assets/btf-count.js): what the customer moves to, never who they are
    if (window.btfTrack) window.btfTrack(this, 'montage', { design: TPL[S.t].name, colour: BG[S.bg][0], size: S.size || '', format: S.fmt === 'land' ? 'Landscape' : S.fmt === 'port' ? 'Portrait' : 'Panoramic' });
    const filled = S.order.slice(0, frames).filter(Boolean).length, low = this.#lowCount(), cm = this.cm;
    const rows = [
      ['Template', tp.name, 'mt-step-2'],
      ['Photos', filled + ' of ' + frames + ' frames filled', 'mt-step-5', filled < frames],
      ['Format', fmtName(S.fmt), 'mt-step-3'],
      ['Size', S.size ? cm[0] + ' × ' + cm[1] + ' cm' : 'Choose a size', 'mt-step-3', !S.size],
      ['Background', BG[S.bg][0], 'mt-step-4'],
    ];
    if (tp.title !== 'none' && S.showTitle && S.title) rows.push(['Title', S.title, 'mt-step-5']);
    rows.push(['Screw caps', (S.caps || capFor(BG[S.bg][1])) === 'white' ? 'White' : 'Black', 'mt-caps']);
    rows.push(['Print check', low ? low + (low === 1 ? ' photo' : ' photos') + ' may print soft' : 'All photos sharp', 'mt-step-5', low > 0]);
    this.q('[data-mt-rows]').innerHTML = rows.map((r) => `<div class="btf-mk-rows__row"><dt class="btf-mk-lbl">${r[0]}</dt><dd${r[3] ? ' class="btf-mk-rows__ask"' : ''}>${esc(r[1])}</dd><a href="#${r[2]}" class="btf-mk-rows__change" data-mt-go="${r[2]}">Change<span class="visually-hidden"> ${r[0].toLowerCase()}</span></a></div>`).join('');
    this.q('[data-mt-price]').textContent = this.#priceText();
    const id = this.q('[data-mt-variant]');
    const v = this.#variant();
    if (id) id.value = v ? v.id : '';
    this.querySelectorAll('[data-mt-cap]').forEach((b) => {
      const on = b.dataset.mtCap === (S.caps || capFor(BG[S.bg][1]));
      b.classList.toggle('is-on', on);
      b.setAttribute('aria-pressed', String(on));
    });
  }

  #ask(text) {
    const m = this.q('[data-mt-msg]');
    m.textContent = text || '';
    m.hidden = !text;
  }

  /* ----------------------------------------------------------------- rooms */

  #watchRooms() {
    const stage = this.q('[data-mt-room-stage]');
    if (!stage || !('IntersectionObserver' in window)) {
      this.#roomsInView = true;
      return;
    }
    const io = new IntersectionObserver((en) => {
      if (en.some((e) => e.isIntersecting)) {
        this.#roomsInView = true;
        io.disconnect();
        this.#drawRoom();
      }
    }, { rootMargin: '600px 0px' });
    io.observe(stage);
  }

  #roomInto(stage, board, st, fmt, room, frame, load, pre) {
    const cm = realCm(st, fmt);
    const large = fmt !== 'pano' && (st.size || 'Large') === 'Large';
    const caps = st.caps || capFor(BG[st.bg][1]);
    this.roomMod.roomLayout(stage, {
      room, cm, port: fmt === 'port', large, frame, caps, load,
      urls: this.setup.rooms[room],
      capUrls: { white: this.setup.assets.capWhite, black: this.setup.assets.capBlack },
    });
    this.#paintBoard(board, Object.assign({}, st, { sel: null }), fmt, pre, false, null);
    board.style.borderRadius = '0';
    board.style.boxShadow = 'none';
  }

  #drawRoom() {
    const S = this.S, stage = this.q('[data-mt-room-stage]');
    if (!stage) return;
    this.#roomInto(stage, this.q('[data-mt-room-board]'), S, S.fmt, S.room, 3 / 2, this.#roomsInView, this.uid + 'rm_');
    this.querySelectorAll('[data-mt-room]').forEach((b) => {
      b.classList.toggle('is-on', b.dataset.mtRoom === S.room);
      b.setAttribute('aria-pressed', String(b.dataset.mtRoom === S.room));
    });
    const cm = this.cm;
    this.q('[data-mt-roomnote]').textContent = S.size ? fmtName(S.fmt) + ', ' + cm[0] + ' × ' + cm[1] + ' cm.' : 'Shown at ' + (S.fmt === 'pano' ? '100 × 40 cm' : 'Large') + ' until you choose a size.';
  }

  /* ------------------------------------------------------------------ hero */

  #heroInit() {
    const hero = document.querySelector('[data-mk-hero]');
    if (!hero) return;
    this.heroEl = hero;
    hero.querySelector('[data-mk-hero-prev]')?.addEventListener('click', () => this.#heroGo(-1, true));
    hero.querySelector('[data-mk-hero-next]')?.addEventListener('click', () => this.#heroGo(1, true));
    hero.querySelector('[data-mk-hero-use]')?.addEventListener('click', () => {
      this.#setTpl(HERO[this.#hero.i]);
      this.#goTo('mt-step-5');
    });
    hero.addEventListener('mouseenter', () => (this.#hero.paused = true));
    hero.addEventListener('mouseleave', () => (this.#hero.paused = false));
    hero.addEventListener('focusin', () => (this.#hero.paused = true));
    hero.addEventListener('focusout', () => (this.#hero.paused = false));
    this.#heroDraw();
    this.#hero.timer = setInterval(() => {
      if (!this.#hero.paused && !document.hidden) this.#heroGo(1, false);
    }, HERO_MS);
  }

  #heroGo(d, user) {
    this.#hero.i = (this.#hero.i + d + HERO.length) % HERO.length;
    if (user) clearInterval(this.#hero.timer);
    this.#heroDraw();
  }

  #heroDraw() {
    const hero = this.heroEl;
    if (!hero) return;
    const i = HERO[this.#hero.i];
    const stage = hero.querySelector('[data-mk-room-stage]');
    const st = Object.assign(this.#thumbState(i, 'land'), { size: 'Large', edge: 'mount' });
    const swap = () => this.#roomInto(stage, stage.querySelector('[data-mk-room-board]'), st, 'land', hero.dataset.room || 'desk', 5 / 4, true, this.uid + 'hr_');
    if (still() || !stage.dataset.drawn) swap();
    else {
      stage.classList.add('is-fading');
      setTimeout(() => {
        swap();
        stage.classList.remove('is-fading');
      }, 220);
    }
    stage.dataset.drawn = '1';
    const n = hero.querySelector('[data-mk-hero-n]');
    if (n) n.textContent = String(this.#hero.i + 1).padStart(2, '0') + ' / ' + HERO.length;
    const name = hero.querySelector('[data-mk-hero-name]');
    if (name) name.textContent = TPL[i].name;
    const bar = hero.querySelector('[data-mk-hero-bar]');
    if (bar) bar.style.width = (((this.#hero.i + 1) / HERO.length) * 100).toFixed(2) + '%';
  }

  /* --------------------------------------------------------------- actions */

  #commit(ins) {
    const S = this.S;
    if (S.sel != null && S.sel !== S._ls) S.pane = 'photo';
    S._ls = S.sel;
    this.#drawPane();
    this.#drawBoard();
    if (ins !== false) this.#drawIns();
    this.#drawTray();
    this.#drawTools();
    this.#drawSummary();
    this.#soon('room', () => this.#drawRoom(), 260);
    if (this.share && this.share.prepare) this.share.prepare();
  }

  #thumbsSoon() {
    this.#soon('thumbs', () => {
      this.#drawTpls();
      this.#heroDraw();
    }, 220);
  }

  #goTo(id) {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: still() ? 'auto' : 'smooth', block: 'start' });
  }

  #place(pid, i) {
    const S = this.S;
    this.#snap();
    const j = S.order.indexOf(pid);
    if (j >= 0) S.order[j] = S.order[i];
    S.order[i] = pid;
    S.sel = i;
    S.hint = '';
    this.#commit();
  }

  #setTpl(i, draw = true) {
    const S = this.S;
    this.#snap();
    S.t = i;
    S.seed = 0;
    S.n = this.#defN(TPL[i]);
    S.sel = null;
    this.#regen();
    if (!draw) return;
    this.#markTpls();
    this.#drawWords();
    this.#commit();
  }

  #autoFill() {
    const S = this.S, P = this.#last;
    this.#snap();
    const ids = this.#fillOrder(S.order, P.cells.length).filter(Boolean);
    const ar = (p) => { const e = E(S, p), ph = S.byId[p]; return e.rot % 180 ? ph.h / ph.w : ph.w / ph.h; };
    const ci = P.cells.map((c, i) => i).sort((a, b) => P.cells[a].w / P.cells[a].h - P.cells[b].w / P.cells[b].h);
    const ps = ids.slice().sort((a, b) => ar(a) - ar(b));
    const out = Array(P.cells.length).fill(null);
    if (ps.length >= ci.length) ci.forEach((c, k) => (out[c] = ps[k]));
    else ps.forEach((p, k) => (out[ci[Math.round((k * (ci.length - 1)) / Math.max(1, ps.length - 1))]] = p));
    S.order = out;
    S.sel = null;
    this.#commit();
  }

  #shuffle() {
    const S = this.S;
    this.#snap();
    S.seed++;
    const r = rng(S.seed * 31 + 7), a = S.order.slice();
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(r() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    S.tree = genTree(S, S.fmt);
    S.order = a;
    S.sel = null;
    this.#commit();
  }

  // A photo in: decoded the right way up, with a copy at most 2000 px long for the editor
  async #load(file, url, name) {
    try {
      const blob = file || (await fetch(url).then((r) => r.blob()));
      const bmp = await createImageBitmap(blob, { imageOrientation: 'from-image' });
      const w = bmp.width, h = bmp.height, k = Math.min(1, 2000 / Math.max(w, h));
      const c = document.createElement('canvas');
      c.width = Math.round(w * k);
      c.height = Math.round(h * k);
      c.getContext('2d').drawImage(bmp, 0, 0, c.width, c.height);
      if (bmp.close) bmp.close();
      const small = await new Promise((r) => c.toBlob(r, 'image/jpeg', 0.9));
      return { src: URL.createObjectURL(small), w, h, name: name || 'Your photo', file: file || blob, url: url || null };
    } catch (e) {
      return null;
    }
  }

  async #addFiles(files, target) {
    const list = Array.from(files).filter((x) => /^image\//.test(x.type));
    if (!list.length) return;
    this.S.hint = 'Adding your photos…';
    this.#drawTray();
    const loaded = (await Promise.all(list.map((f) => this.#load(f, null, f.name.replace(/\.[^.]+$/, ''))))).filter(Boolean);
    this.S.hint = loaded.length < list.length ? 'One or more photos could not be opened. JPEG and PNG work best.' : '';
    if (loaded.length) this.#dropDemo();
    if (loaded.length && window.btfCount) window.btfCount('montage', 'photo', String(loaded.length), true);
    this.#addMany(loaded, target);
  }

  #addMany(list, target) {
    const S = this.S, room = MAX - S.photos.length;
    if (list.length > room) S.hint = MAX + ' photos is the most for one montage.';
    const ids = list.slice(0, room).map((p) => {
      const id = 'p' + ++this.#pc;
      S.photos.push(Object.assign({ id }, p));
      S.byId[id] = S.photos[S.photos.length - 1];
      return id;
    });
    if (!ids.length) {
      this.#drawTray();
      return;
    }
    this.#snap();
    const tp = this.tp;
    const rest = ids.slice();
    if (target != null) {
      S.order[target] = rest.shift();
      S.sel = target;
    }
    S.order = S.order.map((p) => p || (rest.length ? rest.shift() : null));
    if (rest.length && !tp.fixed && S.n < tp.max) {
      S.n = Math.min(tp.max, S.n + rest.length);
      this.#regen();
    }
    this.#commit();
    this.#drawAlbum();
    this.#thumbsSoon();
  }

  #removePhoto(pid) {
    const S = this.S, tp = this.tp;
    this.#snap();
    const i = S.order.indexOf(pid);
    S.photos = S.photos.filter((p) => p.id !== pid);
    delete S.byId[pid];
    if (i >= 0) {
      if (!tp.fixed && S.n > tp.min) {
        S.order.splice(i, 1);
        S.n--;
        S.tree = genTree(S, S.fmt);
        S.order = S.order.slice(0, S.n);
      } else S.order[i] = null;
    }
    if (S.sel != null && S.sel >= S.n) S.sel = null;
    this.#commit();
    this.#drawAlbum();
    this.#thumbsSoon();
  }

  /* ---------------------------------------------------------------- events */

  #listen() {
    const S = this.S, board = () => this.board;
    // Board gestures: select, swap by dragging, pan the selected photo, resize frames
    this.addEventListener('pointerdown', (ev) => {
      if (ev.button > 0 || !board().contains(ev.target)) return;
      const P = this.#last, k = 1000 / board().getBoundingClientRect().width;
      const dv = ev.target.closest('[data-div]'), cell = ev.target.closest('[data-cell]');
      if (dv && P.divs[+dv.dataset.div]) {
        this.#snap();
        const d = P.divs[+dv.dataset.div];
        this.#drag = { type: 'div', d, x0: ev.clientX, y0: ev.clientY, r0: d.nd.r.slice(), k };
      } else if (cell) {
        const i = +cell.dataset.cell, pid = S.order[i];
        if (S.sel === i && pid) {
          this.#snap();
          const e = E(S, pid);
          this.#drag = { type: 'pan', i, pid, x0: ev.clientX, y0: ev.clientY, px: e.px, py: e.py, k, a: imgA(P.cells[i], S.byId[pid], e) };
        } else this.#drag = { type: 'swap', i, x0: ev.clientX, y0: ev.clientY, moved: false, over: null };
      } else {
        if (S.sel != null) {
          S.sel = null;
          this.#commit();
        }
        return;
      }
      try {
        board().setPointerCapture(ev.pointerId);
      } catch (er) {}
      ev.preventDefault();
    });
    const setOp = (i, v) => {
      const g = board().querySelector('[data-cell="' + i + '"]');
      if (g) g.setAttribute('opacity', v);
    };
    this.addEventListener('pointermove', (ev) => {
      const drag = this.#drag;
      if (!drag) return;
      const dx = (ev.clientX - drag.x0) * (drag.k || 1), dy = (ev.clientY - drag.y0) * (drag.k || 1);
      if (drag.type === 'div') {
        const d = drag.d, a = drag.r0[d.i], b = drag.r0[d.i + 1], mn = d.sum * 0.08, na = clamp(a + ((d.horiz ? dx : dy) / d.len) * d.sum, mn, a + b - mn);
        d.nd.r[d.i] = na;
        d.nd.r[d.i + 1] = a + b - na;
        this.#rafBoard();
      } else if (drag.type === 'pan') {
        const e = E(S, drag.pid);
        if (drag.a.ox > 0.5) e.px = clamp(drag.px + dx / drag.a.ox, -1, 1);
        if (drag.a.oy > 0.5) e.py = clamp(drag.py + dy / drag.a.oy, -1, 1);
        this.#liveImg(drag.i);
      } else if (drag.type === 'swap') {
        if (!drag.moved && Math.hypot(ev.clientX - drag.x0, ev.clientY - drag.y0) > 6) {
          if (!S.order[drag.i]) {
            drag.type = 'none';
            return;
          }
          drag.moved = true;
          setOp(drag.i, 0.45);
        }
        if (drag.moved) {
          const el = document.elementFromPoint(ev.clientX, ev.clientY), c2 = el && el.closest && el.closest('[data-cell]'), t = c2 && board().contains(c2) ? +c2.dataset.cell : null;
          if (t !== drag.over) {
            if (drag.over != null && drag.over !== drag.i) setOp(drag.over, 1);
            drag.over = t;
            if (t != null && t !== drag.i) setOp(t, 0.7);
          }
        }
      }
    });
    const end = () => {
      const d = this.#drag;
      if (!d) return;
      this.#drag = null;
      if (d.type === 'swap') {
        if (d.moved && d.over != null && d.over !== d.i) {
          this.#snap();
          [S.order[d.i], S.order[d.over]] = [S.order[d.over], S.order[d.i]];
          S.sel = d.over;
        } else if (!d.moved) S.sel = S.sel === d.i ? null : d.i;
        else setOp(d.i, 1);
        S.hint = '';
      }
      if (d.type !== 'none') this.#commit(d.type === 'pan' ? true : undefined);
    };
    this.addEventListener('pointerup', end);
    this.addEventListener('pointercancel', end);
    this.addEventListener('wheel', (ev) => {
      if (S.sel == null || !S.order[S.sel] || !board().contains(ev.target)) return;
      const c = ev.target.closest('[data-cell]');
      if (!c || +c.dataset.cell !== S.sel) return;
      ev.preventDefault();
      const e = E(S, S.order[S.sel]);
      e.z = clamp(e.z * Math.exp(-ev.deltaY * 0.0015), 1, 4);
      this.#liveImg(S.sel);
      const zr = this.q('[data-mt-ik="z"]');
      if (zr) {
        zr.value = e.z;
        this.q('[data-mt-iv="z"]').textContent = Math.round(e.z * 100) + '%';
      }
      this.#soon('wheel', () => this.#commit(), 260);
    }, { passive: false });

    // Drag and drop: tray photos onto frames, and files from the computer anywhere in the maker
    this.addEventListener('dragstart', (ev) => {
      const t = ev.target.closest && ev.target.closest('[data-mt-tray]');
      if (t) {
        ev.dataTransfer.setData('text/x-mt', t.dataset.mtTray);
        ev.dataTransfer.effectAllowed = 'move';
      }
    });
    this.addEventListener('dragover', (ev) => {
      const types = Array.from(ev.dataTransfer.types || []);
      const files = types.includes('Files'), own = types.includes('text/x-mt');
      if (!files && !own) return;
      ev.preventDefault();
      this.q('[data-mt-tray]').classList.toggle('is-over', files);
    });
    this.addEventListener('dragleave', (ev) => {
      if (!this.contains(ev.relatedTarget)) this.q('[data-mt-tray]').classList.remove('is-over');
    });
    this.addEventListener('drop', (ev) => {
      this.q('[data-mt-tray]').classList.remove('is-over');
      const pid = ev.dataTransfer.getData('text/x-mt');
      const el = document.elementFromPoint(ev.clientX, ev.clientY), c = el && el.closest && el.closest('[data-cell]');
      const target = c && board().contains(c) ? +c.dataset.cell : null;
      if (pid) {
        ev.preventDefault();
        if (target != null) this.#place(pid, target);
        return;
      }
      if (ev.dataTransfer.files && ev.dataTransfer.files.length) {
        ev.preventDefault();
        this.#addFiles(ev.dataTransfer.files, target);
      }
    });

    this.addEventListener('input', (ev) => {
      const t = ev.target, k = t.dataset.mtK, ik = t.dataset.mtIk;
      if (k === 'gap' || k === 'border') {
        S[k] = +t.value;
        this.q('[data-mt-v="' + k + '"]').textContent = t.value;
        this.#rafBoard();
        this.#soon('commit', () => { this.#commit(false); this.#thumbsSoon(); }, 200);
        return;
      }
      if (k === 'tsz') {
        S.tsz = +t.value;
        this.q('[data-mt-tszv]').textContent = t.value + '%';
        this.#rafBoard();
        this.#soon('commit', () => { this.#commit(false); this.#thumbsSoon(); }, 200);
        return;
      }
      if (k) {
        S[k] = t.value;
        const tp = this.tp;
        if (tp.fixed && ['word', 'initials'].includes(k) && tp.fixed(S) !== S.n) {
          S.n = tp.fixed(S);
          this.#regen();
        }
        this.#soon('commit', () => { this.#commit(); this.#thumbsSoon(); }, 160);
        return;
      }
      if (ik && S.sel != null && S.order[S.sel]) {
        const e = E(S, S.order[S.sel]);
        if (ik === 'cap') {
          e.cap = t.value;
          this.q('[data-mt-cc]').textContent = t.value.length + ' / 28';
        } else {
          e[ik] = +t.value;
          this.q('[data-mt-iv="' + ik + '"]').textContent = ik === 'z' ? Math.round(e.z * 100) + '%' : t.value;
          this.#liveImg(S.sel);
        }
        this.#soon('commit', () => {
          this.#commit(false);
          const qb = this.q('[data-mt-qbox]');
          if (qb) qb.outerHTML = this.#qbox();
        }, 200);
      }
    });
    this.addEventListener('change', (ev) => {
      const t = ev.target;
      if (t.dataset.mtFiles !== undefined && t.files.length) {
        this.#addFiles(t.files, null).then(() => (t.value = ''));
      }
      if (t.dataset.mtReplace !== undefined && t.files.length) {
        const i = S.sel;
        if (S.photos.length >= MAX) {
          S.hint = MAX + ' photos is the most for one montage.';
          this.#drawTray();
          return;
        }
        this.#load(t.files[0], null, t.files[0].name.replace(/\.[^.]+$/, '')).then((p) => {
          if (!p) return;
          const id = 'p' + ++this.#pc;
          S.photos.push(Object.assign({ id }, p));
          S.byId[id] = S.photos[S.photos.length - 1];
          this.#place(id, i);
          this.#thumbsSoon();
        });
      }
      if (t.dataset.mtIk && t.type === 'range') this.#snap();
    });

    this.addEventListener('click', (ev) => {
      const c = (s) => ev.target.closest(s);
      const e0 = () => E(S, S.order[S.sel]);
      let x;
      if ((x = c('[data-mt-zoom]'))) return this.#lbOpen(+x.dataset.mtZoom);
      if ((x = c('[data-mt-rm]'))) {
        ev.stopPropagation();
        return this.#removePhoto(x.dataset.mtRm);
      }
      if ((x = c('[data-mt-tray]'))) {
        const pid = x.dataset.mtTray;
        if (S.sel != null) this.#place(pid, S.sel);
        else {
          const k = S.order.indexOf(null);
          if (S.order.includes(pid)) {
            S.sel = S.order.indexOf(pid);
            this.#commit();
          } else if (k >= 0) this.#place(pid, k);
          else {
            S.hint = 'Select a frame on the board, then tap a photo to put it there.';
            this.#drawTray();
          }
        }
        return;
      }
      if (c('[data-mt-album]')) {
        S.album = !S.album;
        return this.#drawAlbum();
      }
      if ((x = c('[data-mt-sample]'))) {
        const s = this.setup.samples[+x.dataset.mtSample];
        x.disabled = true;
        return this.#load(null, s.src, s.name).then((p) => p && this.#addMany([Object.assign(p, { url: s.src, sample: true })], S.sel != null && !S.order[S.sel] ? S.sel : null));
      }
      if (c('[data-mt-clear]')) {
        this.#snap();
        S.photos = [];
        S.byId = {};
        S.order = S.order.map(() => null);
        S.sel = null;
        this.#commit();
        this.#drawAlbum();
        return this.#thumbsSoon();
      }
      if ((x = c('[data-mt-fam]'))) {
        S.fam = x.dataset.mtFam;
        this.#drawFams();
        return this.#drawTpls();
      }
      if (c('[data-mt-tall]')) {
        const was = S.allT;
        S.allT = !S.allT;
        this.#drawTpls();
        if (was) this.#goTo('mt-step-2');
        return;
      }
      if ((x = c('[data-mt-tpl]'))) return this.#setTpl(+x.dataset.mtTpl);
      if ((x = c('[data-mt-fset]'))) {
        const o = x.dataset.mtFset;
        if (o === S.fmt) return;
        // A size belongs to its format: panoramic boards have their own two
        if ((o === 'pano') !== (S.fmt === 'pano')) S.size = null;
        S.fmt = o;
        S.tree = genTree(S, S.fmt);
        this.#drawFmt();
        this.#commit();
        return this.#thumbsSoon();
      }
      if ((x = c('[data-mt-size]'))) {
        S.size = x.dataset.mtSize;
        this.#ask('');
        this.#drawFmt();
        return this.#commit();
      }
      if ((x = c('[data-mt-bg]'))) {
        S.bg = +x.dataset.mtBg;
        S.caps = null;
        this.#drawColours();
        this.#commit();
        return this.#thumbsSoon();
      }
      if ((x = c('[data-mt-bc]'))) {
        S.bc = +x.dataset.mtBc;
        this.#drawColours();
        this.#commit();
        return this.#thumbsSoon();
      }
      if ((x = c('[data-mt-edge]'))) {
        S.edge = x.dataset.mtEdge;
        this.#drawColours();
        this.#commit();
        return this.#thumbsSoon();
      }
      if ((x = c('[data-mt-pane]'))) {
        S.pane = x.dataset.mtPane;
        return this.#drawPane();
      }
      if ((x = c('[data-mt-al]'))) {
        const [k, v] = x.dataset.mtAl.split('|');
        S[k] = v;
        this.#drawWords();
        this.#commit();
        return this.#thumbsSoon();
      }
      if ((x = c('[data-mt-ttl]'))) {
        S.showTitle = x.dataset.mtTtl === '1';
        this.#drawWords();
        this.#commit();
        return this.#thumbsSoon();
      }
      if ((x = c('[data-mt-act]'))) {
        const a = x.dataset.mtAct, tp = this.tp;
        if (a === 'auto') this.#autoFill();
        else if (a === 'shuffle') this.#shuffle();
        else if (a === 'add' && !tp.fixed && S.n < tp.max) {
          this.#snap();
          S.n++;
          this.#regen();
          this.#commit();
        } else if (a === 'remove' && !tp.fixed && S.n > tp.min) {
          this.#snap();
          if (S.sel != null) S.order.splice(S.sel, 1);
          else {
            const k = S.order.lastIndexOf(null);
            S.order.splice(k >= 0 ? k : S.order.length - 1, 1);
          }
          S.n--;
          S.sel = null;
          this.#regen();
          this.#commit();
        } else if (a === 'undo' && S.hist.length) {
          Object.assign(S, JSON.parse(S.hist.pop()));
          S.order = S.order.map((p) => (p && S.byId[p] ? p : null));
          S.sel = null;
          this.#markTpls();
          this.#drawWords();
          this.#commit();
        }
        return;
      }
      if ((x = c('[data-mt-ia]'))) {
        const a = x.dataset.mtIa;
        if (a === 'done') {
          S.sel = null;
          return this.#commit();
        }
        if (a === 'clear') {
          this.#snap();
          S.order[S.sel] = null;
          return this.#commit();
        }
        const e = e0();
        this.#snap();
        if (a === 'rl') e.rot -= 90;
        else if (a === 'rr') e.rot += 90;
        else if (a === 'flip') e.flip = !e.flip;
        else if (a === 'reset') {
          e.z = 1;
          e.px = 0;
          e.py = 0;
        }
        return this.#commit();
      }
      if ((x = c('[data-mt-filt]'))) {
        this.#snap();
        e0().f = x.dataset.mtFilt;
        return this.#commit();
      }
      if ((x = c('[data-mt-cap]'))) {
        S.caps = x.dataset.mtCap;
        return this.#commit(false);
      }
      if ((x = c('[data-mt-room]'))) {
        S.room = x.dataset.mtRoom;
        return this.#drawRoom();
      }
      if ((x = c('[data-mt-go]'))) {
        ev.preventDefault();
        return this.#goTo(x.dataset.mtGo);
      }
    });

    this.addEventListener('keydown', (ev) => {
      if (this.lb != null) this.#lbKeys(ev);
    });
  }

  /* ---------------------------------------------- enlarge a template */

  #lbOpen(i) {
    this.lb = i;
    this.lbBack = document.activeElement;
    document.documentElement.classList.add('btf-mk-locked');
    this.#lbDraw();
    this.q('[data-mt-lbx]')?.focus();
  }

  #lbClose() {
    this.lb = null;
    const el = this.q('[data-mt-lb]');
    el.hidden = true;
    el.innerHTML = '';
    document.documentElement.classList.remove('btf-mk-locked');
    if (this.lbBack && this.lbBack.focus) this.lbBack.focus();
  }

  #lbDraw() {
    const S = this.S, i = this.lb, tp = TPL[i], el = this.q('[data-mt-lb]');
    const n = tp.fixed ? tp.fixed(S) : tp.min + '–' + tp.max;
    el.hidden = false;
    el.innerHTML = `
      <div class="btf-mk-lb__panel" role="dialog" aria-modal="true" aria-label="${esc(tp.name)}">
        <div class="btf-mk-lb__head"><span class="btf-mk-lb__title"><span>${String(i + 1).padStart(2, '0')} of ${TPL.length}</span>${esc(tp.name)}</span><button type="button" class="btf-mk-xb" data-mt-lbx>${I.x}<span>Close</span></button></div>
        <div class="btf-mk-lb__stage"><button type="button" class="btf-mk-lb__nav" data-mt-lbgo="-1" aria-label="Previous template">${I.prev}</button><div class="btf-mk-lb__wrap"><div class="btf-mk-board" data-mt-lbboard></div></div><button type="button" class="btf-mk-lb__nav" data-mt-lbgo="1" aria-label="Next template">${I.next}</button></div>
        <div class="btf-mk-lb__foot"><span>${esc(tp.desc)} · ${n} photos${i === S.t ? ' · <b>Your current template</b>' : ''}</span><button type="button" class="btf-mk-btn" data-mt-lbuse>Use this template</button></div>
        <div class="btf-mk-lb__strip btf-mt-lbstrip">${TPL.map((t, j) => `<button type="button" class="${j === i ? 'is-on' : ''}" data-mt-lbto="${j}" title="${esc(t.name)}">${String(j + 1).padStart(2, '0')}</button>`).join('')}</div>
      </div>`;
    this.#paintBoard(el.querySelector('[data-mt-lbboard]'), this.#thumbState(i), S.fmt, this.uid + 'lb_', false, S.caps || capFor(BG[S.bg][1]));
    el.onclick = (e) => {
      const t = e.target instanceof Element ? e.target : null;
      if (!t) return;
      if (t === el || t.closest('[data-mt-lbx]')) return this.#lbClose();
      const go = t.closest('[data-mt-lbgo]'), to = t.closest('[data-mt-lbto]');
      if (go) this.#lbMove(+go.dataset.mtLbgo);
      else if (to) this.#lbMove(+to.dataset.mtLbto - this.lb);
      else if (t.closest('[data-mt-lbuse]')) {
        const j = this.lb;
        this.#lbClose();
        this.#setTpl(j);
        this.#goTo('mt-step-5');
      }
    };
    let x0 = null;
    el.onpointerdown = (e) => (x0 = e.clientX);
    el.onpointerup = (e) => {
      if (x0 == null) return;
      const d = e.clientX - x0;
      x0 = null;
      if (Math.abs(d) > 50) this.#lbMove(d < 0 ? 1 : -1);
    };
  }

  #lbMove(d) {
    this.lb = (this.lb + d + TPL.length) % TPL.length;
    this.#lbDraw();
  }

  #lbKeys(e) {
    if (e.key === 'Escape') this.#lbClose();
    else if (e.key === 'ArrowRight') this.#lbMove(1);
    else if (e.key === 'ArrowLeft') this.#lbMove(-1);
    else if (e.key === 'Tab') {
      const f = [...this.querySelectorAll('[data-mt-lb] button')];
      if (!f.length) return;
      if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
      else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
    }
  }

  /* -------------------------------------------------------- print and basket */

  #printInput() {
    const S = this.S;
    return {
      layout: L,
      state: Object.assign({}, S, { sel: null }),
      fmt: S.fmt,
      board: { w: this.cm[0] * 10, h: this.cm[1] * 10 },
      fonts: this.setup.fonts,
      libs: { pdfLib: this.setup.assets.pdfLib, fontkit: this.setup.assets.fontkit },
      capUrl: (S.caps || capFor(BG[S.bg][1])) === 'black' ? this.setup.assets.capBlack : this.setup.assets.capWhite,
    };
  }

  async #picture() {
    const { buildPicture } = await import(this.setup.assets.print);
    return buildPicture(this.#printInput());
  }

  async #submit(e) {
    const form = e.target;
    if (!this.form || form !== this.form) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    if (this.#busy) return;
    const S = this.S, P = this.#last;
    const frames = P.cells.length, filled = S.order.slice(0, frames).filter(Boolean).length;
    if (!S.size) {
      // The sizes come to the button, so the page stays where the customer is
      this.#ask('Choose a size first.');
      const msg = this.q('[data-mt-msg]'), sizes = this.q('[data-mt-sizes]');
      if (msg && sizes) {
        const copy = sizes.cloneNode(true);
        copy.removeAttribute('data-mt-sizes');
        copy.classList.add('btf-mk-sizes--here');
        msg.append(copy);
      }
      return;
    }
    if (filled < frames) {
      this.#ask('Fill the empty frames, or remove them, before you order.');
      return this.#goTo('mt-step-5');
    }
    if (S.order.slice(0, frames).some((id) => id && S.byId[id] && S.byId[id].sample)) {
      this.#ask('Swap the sample photos for your own before you order. They are there to show the templates.');
      return this.#goTo('mt-step-1');
    }
    this.#ask('');
    // The customer's go-ahead, ticked beside the button (assets/btf-print-jpeg.js)
    const PJ = await import((window.btfAssets && window.btfAssets.printJpeg) || './btf-print-jpeg.js');
    const approved = PJ.approval(this);
    if (approved === null) return;
    this.#busy = true;
    const button = form.querySelector('[type="submit"]');
    const label = button ? button.innerHTML : '';
    const say = (t) => button && (button.textContent = t);
    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }
    say('Preparing your artwork…');
    const v = this.#variant(), cm = this.cm, tp = this.tp;
    const stamp = new Date().toISOString().slice(0, 16).replace(/[-:]/g, '').replace('T', '-');
    const base = `photo-montage-${tp.id}-${cm[0]}x${cm[1]}-${stamp}`;
    const props = {
      Template: tp.name,
      Format: fmtName(S.fmt),
      Photos: String(filled),
      Background: BG[S.bg][0],
    };
    if (tp.title !== 'none' && S.showTitle && S.title) props.Title = S.title;
    if (tp.title !== 'none' && S.showTitle && S.date) props[tp.id === 'year' ? 'Line' : 'Date'] = S.date;
    if (tp.id === 'word') props.Word = L.letters(S.word, 6);
    if (tp.id === 'initials') props.Initials = L.letters(S.initials, 3);
    if (tp.id === 'number') props.Number = L.letters(S.number, 4);
    const caps = S.order.slice(0, frames).map((p) => p && E(S, p).cap).filter(Boolean);
    if (caps.length) props.Captions = caps.join(' · ');
    props['Screw caps'] = (S.caps || capFor(BG[S.bg][1])) === 'black' ? 'Black' : 'White';
    const low = this.#lowCount();
    if (low) props['Print check'] = low + (low === 1 ? ' photo' : ' photos') + ' under 150 dpi';
    props._maker = 'montage';
    props._maker_version = '1';
    // No _edit_url: the photos live only in this browser, so a basket line cannot be reopened
    props._montage = JSON.stringify({ t: tp.id, fmt: S.fmt, size: S.size, bg: S.bg, bc: S.bc, gap: S.gap, border: S.border, edge: S.edge, seed: S.seed, n: S.n, tree: S.tree, title: S.showTitle ? [S.title, S.date, S.tsz, S.ta || '', S.da || ''] : null, photos: S.order.slice(0, frames).map((p) => { const ph = S.byId[p], e = E(S, p); return [ph.name, ph.w, ph.h, e.z, +e.px.toFixed(3), +e.py.toFixed(3), e.rot, e.flip ? 1 : 0, e.f, e.b, e.c]; }) }).slice(0, 4000);

    let files = null, pending = '';
    try {
      const { buildMontage } = await import(this.setup.assets.print);
      files = await buildMontage(Object.assign(this.#printInput(), {
        progress: (t) => say(t),
        meta: { title: `Photo montage · ${tp.name} · ${BG[S.bg][0]} · ${cm[0] * 10} × ${cm[1] * 10} mm`, subject: props.Title || '', keywords: ['Photo montage', tp.name, fmtName(S.fmt), filled + ' photos'], creator: 'Beyond the Fridge · Photo montage · layout 1' },
      }));
    } catch (err) {
      pending = `yes — the print file was not made in the browser (${err && err.message ? err.message : 'unknown error'}); ask the customer for the photos`;
    }
    let post;
    try {
      post = await import(this.setup.assets.cartPost);
    } catch (err) {
      this.#busy = false;
      if (button) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        button.innerHTML = label;
      }
      return this.#ask('Something went wrong adding your board. Please try again.');
    }
    // The print file, the proof and the approval first, so the order reads top down
    const lineData = (withFiles) => {
      const fd = new FormData();
      fd.set('id', v.id);
      fd.set('quantity', '1');
      if (withFiles && files && files.flat) fd.append(`properties[${PJ.PRINT_FILE}]`, files.flat, base + '-print-150dpi.jpg');
      if (approved) fd.append(`properties[${PJ.APPROVED}]`, approved);
      Object.entries(props).forEach(([k, val]) => fd.append(`properties[${k}]`, val));
      return fd;
    };
    say('Adding to your basket…');
    let res = null, reason = '';
    // One file: the artwork. Only if it could not be drawn does the PDF go instead, with the
    // small picture for the basket.
    for (const once of [true]) {
      try {
        const fd = lineData(once);
        if (files && !files.flat) {
          fd.append('properties[_Artwork PDF (print from this)]', files.pdf, base + '.pdf');
          fd.append('properties[Your artwork]', files.png, base + '-preview.png');
        } else if (!files) fd.append('properties[_artwork_pending]', pending);
        res = await post.postForm('/cart/add.js', fd, (p) => say(`Adding to your basket… ${Math.round(p * 100)}%`));
        reason = res ? 'status ' + res.status : 'no response';
      } catch (err) {
        reason = err && err.message ? err.message : 'the upload failed';
      }
      if (post.added(res)) break;
    }
    if (!post.added(res) && files) {
      const fd = lineData();
      fd.append('properties[_artwork_pending]', `yes — the print file did not attach (${reason}); ask the customer for the photos`);
      try {
        res = await post.postForm('/cart/add.js', fd, null);
      } catch (err) {
        res = null;
      }
    }
    if (post.added(res)) {
      await PJ.tidyLine(res.json);
      await post.announce({ variantId: v.id, productId: '', sourceId: 'btf-montage' });
      window.location.assign('/cart');
      return;
    }
    this.#busy = false;
    if (button) {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.innerHTML = label;
    }
    this.#ask((res && res.json && (res.json.description || res.json.message)) || 'Something went wrong adding your board. Please try again.');
  }
}

if (!customElements.get('btf-montage')) customElements.define('btf-montage', BtfMontage);
